import type {
  ActivityFlagSeverity,
  ActivityInsightFlag,
  ActivityInsightTransition,
} from '../types/usage-insights.types';

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export function formatEventLabel(eventType: string): string {
  return eventType
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatFlagCodeHeadline(code: string): string {
  return formatEventLabel(code);
}

export function getSeverityRank(severity: ActivityFlagSeverity | number): number {
  if (typeof severity === 'number') {
    return severity;
  }
  return SEVERITY_ORDER[severity] ?? 99;
}

export function sortFlagsBySeverity(flags: ActivityInsightFlag[]): ActivityInsightFlag[] {
  return [...flags].sort((left, right) => getSeverityRank(left.severity) - getSeverityRank(right.severity));
}

export function roundDisplayNumber(value: number): number {
  return Math.round(value);
}

export function formatMetricValue(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  if (!Number.isFinite(value)) {
    return '—';
  }
  return String(roundDisplayNumber(value));
}

export function formatRatePercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value) || !Number.isFinite(value)) {
    return '—';
  }
  return `${roundDisplayNumber(value * 100)}%`;
}

export function formatGenerationsPerDownload(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value) || !Number.isFinite(value)) {
    return '—';
  }
  return String(roundDisplayNumber(value));
}

export function formatEvidenceValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return '—';
    }
    return String(roundDisplayNumber(value));
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

export function normaliseTransition(transition: ActivityInsightTransition & {
  from?: string;
  to?: string;
}): ActivityInsightTransition {
  return {
    from_event: transition.from_event ?? transition.from ?? '',
    to_event: transition.to_event ?? transition.to ?? '',
    count: transition.count,
  };
}

export function getDefaultDateRange(): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 29);
  return {
    from: formatIsoDate(start),
    to: formatIsoDate(now),
  };
}

export function formatIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isActivityInsightsNotAuthorised(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const httpStatus = (error as { httpStatus?: number }).httpStatus;
  return httpStatus === 403 || httpStatus === 404;
}

export function getActivityInsightsErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return 'Could not load usage insights. Please try again.';
  }

  const responseData = (error as { responseData?: Record<string, unknown> }).responseData;
  const errorCode = typeof responseData?.error_code === 'string' ? responseData.error_code : null;
  const detail = typeof responseData?.detail === 'string' ? responseData.detail : null;

  if (errorCode === 'flow_sync_not_configured') {
    return 'Usage insights are not configured for this environment (missing Flow sync settings).';
  }
  if (errorCode === 'upstream_auth_failed') {
    return 'Could not authenticate with Flow. Check FLOW_SYNC_SECRET / PORTAL_SYNC_SECRET on Portal QA.';
  }
  if (detail) {
    return detail;
  }

  const httpStatus = (error as { httpStatus?: number }).httpStatus;
  if (httpStatus === 502 || httpStatus === 503 || httpStatus === 504) {
    return 'Usage insights are temporarily unavailable. Please try again shortly.';
  }

  return 'Could not load usage insights. Please try again.';
}

export function getSeverityBadgeClass(severity: ActivityFlagSeverity | number): string {
  if (typeof severity === 'number') {
    if (severity <= 0) return 'bg-[#FEF2F2] text-[#DC2626] border-[#FECACA]';
    if (severity <= 1) return 'bg-[#FFF7ED] text-[#EA580C] border-[#FED7AA]';
    if (severity <= 2) return 'bg-[#FFFBEB] text-[#D97706] border-[#FDE68A]';
    return 'bg-[#F0F9FF] text-[#0284C7] border-[#BAE6FD]';
  }

  switch (severity) {
    case 'critical':
      return 'bg-[#FEF2F2] text-[#DC2626] border-[#FECACA]';
    case 'high':
      return 'bg-[#FFF7ED] text-[#EA580C] border-[#FED7AA]';
    case 'medium':
      return 'bg-[#FFFBEB] text-[#D97706] border-[#FDE68A]';
  }
  return 'bg-[#F0F9FF] text-[#0284C7] border-[#BAE6FD]';
}

export function getSeverityLabel(severity: ActivityFlagSeverity | number): string {
  if (typeof severity === 'number') {
    return `Severity ${severity}`;
  }
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}
